import React from 'react'
import { LuCheck } from 'react-icons/lu'

import ServiceMissingWarning from '@/components/misc/ImportReferrals/ServiceMissingWarning'
import ServiceNewWarning from '@/components/misc/ImportReferrals/ServiceNewWarning'
import Select from '@/components/select/Select'
import { Button } from '@/components/ui/button'
import {
  ReferralServiceAreaOption,
  useReferralServiceAreaOptions,
} from '@/hooks/useReferralServiceAreaOptions'
import { REFERRAL_IMPORT_DIRECTION } from '@/lib/constants'
import {
  BulkUpdatePayload,
  ReferralsImportItem,
  ReferralsImportItemPartial,
} from '@/redux/slices/referralsImportBase'
import { HydraResource } from '@/types/api'
import { ServiceAreaEntity } from '@/types/entities'

interface Props {
  importItem: ReferralsImportItem
  updateImportItem: (item: ReferralsImportItemPartial) => void
  bulkUpdateItems: (payload: BulkUpdatePayload) => void
  bulkUpdateSelectedOnly: boolean
}

const ServiceCell: React.FC<Props> = ({
  importItem,
  updateImportItem,
  bulkUpdateItems,
  bulkUpdateSelectedOnly,
}) => {
  const [isEditing, setIsEditing] = React.useState(false)
  const isSet = !!importItem.serviceArea
  const isNew = importItem.newServiceAreaId != null

  const { serviceAreaOptions } = useReferralServiceAreaOptions({
    company: importItem.toCompany || undefined,
  })

  function handleChange(selectedOption: ReferralServiceAreaOption | null) {
    const serviceArea: HydraResource<
      Pick<ServiceAreaEntity, 'id' | 'name'>
    > | null = selectedOption?.value
      ? {
          '@context': '',
          '@id': '',
          '@type': '',
          id: selectedOption.value.id,
          name: selectedOption.value.name,
        }
      : null
    const newServiceAreaId =
      selectedOption &&
      selectedOption.isNew &&
      !selectedOption.value.isFcaServiceArea &&
      importItem.importingSource == REFERRAL_IMPORT_DIRECTION.TO
        ? selectedOption.value.id
        : null

    updateImportItem({
      refUUID: importItem.refUUID,
      serviceArea,
      newServiceAreaId,
      serviceAreaDefaultUsed: false,
    })

    setIsEditing(false)
  }

  function handleBulkApply() {
    bulkUpdateItems({
      filter: {
        selected: bulkUpdateSelectedOnly,
        toCompany: importItem.toCompany?.id,
      },
      data: {
        newServiceAreaId: importItem.newServiceAreaId,
        serviceArea: importItem.serviceArea,
        serviceAreaDefaultUsed: false,
      },
    })
  }

  const Warning =
    importItem.serviceAreaDefaultUsed && importItem.sourceServiceAreaId ? (
      <ServiceMissingWarning importItem={importItem} />
    ) : isNew ? (
      <ServiceNewWarning />
    ) : null

  const Name = (
    <>
      {importItem.serviceArea?.name || ''}
      {importItem.serviceAreaDefaultUsed && (
        <>
          <span className="text-warning text-xs"> (default)</span>
        </>
      )}
    </>
  )

  return (
    <>
      {!isEditing && isSet ? (
        <>
          <span onClick={() => setIsEditing(true)} className="cursor-pointer">
            {Name}
          </span>

          {Warning}

          <span className="show-on-row-hover text-xs">
            {', '}
            <Button variant="plain" onClick={() => setIsEditing(true)}>
              change
            </Button>

            <BulkUpdateButton
              bulkUpdateSelectedOnly={bulkUpdateSelectedOnly}
              importItem={importItem}
            >
              {({ title, label }) => (
                <>
                  {' or '}
                  <Button
                    variant="plain"
                    onClick={handleBulkApply}
                    title={title}
                  >
                    {label}
                  </Button>
                </>
              )}
            </BulkUpdateButton>
          </span>
        </>
      ) : (
        <>
          <Select
            placeholder="Select service..."
            options={serviceAreaOptions}
            onChange={handleChange}
            onBlur={() => setIsEditing(false)}
            value={serviceAreaOptions.find(
              (option) => option.value.id == importItem.serviceArea?.id,
            )}
            className="mb-1"
          />

          {!importItem.serviceArea && (
            <ServiceMissingWarning importItem={importItem} afterBlockElement />
          )}

          <span className="mt-1 block text-right">
            <Button
              size="icon-xs"
              variant="outline-primary"
              onClick={() => {
                setIsEditing(false)
              }}
            >
              <LuCheck />
            </Button>
          </span>
        </>
      )}
    </>
  )
}

function BulkUpdateButton({
  importItem,
  bulkUpdateSelectedOnly,
  children,
}: {
  importItem: ReferralsImportItem
  bulkUpdateSelectedOnly: boolean
  children: ({
    title,
    label,
  }: {
    title: string
    label: React.ReactNode
  }) => React.ReactNode
}) {
  const bulkUpdateScope =
    importItem.bulkToCompanyCount > 1
      ? !bulkUpdateSelectedOnly
        ? 'all'
        : importItem.selected
          ? 'selected'
          : 'none'
      : 'none'
  if (bulkUpdateScope == 'all') {
    return children({
      title: 'Apply this service for all referrals for this company',
      label: (
        <>
          apply&nbsp;to&nbsp;all&nbsp;for&nbsp;this&nbsp;company&nbsp;(
          {importItem.bulkToCompanyCount})
        </>
      ),
    })
  } else if (bulkUpdateScope == 'selected') {
    return children({
      title: 'Apply this service for selected referrals for this company',
      label: <>apply&nbsp;to&nbsp;selected&nbsp;for&nbsp;this&nbsp;company</>,
    })
  } else {
    return <></>
  }
}

export default ServiceCell
